// src/Product/PDF/EstimateInvoicePDF.jsx
import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
} from "@react-pdf/renderer";

const money = (n, currency = "NGN") => {
  const v = Number(n || 0);
  const fmt = v.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const symbol = currency === "NGN" ? "₦" : "";
  return `${symbol}${fmt}`;
};

const safe = (v, fallback = "—") => (v ? String(v) : fallback);

const isPaid = (status) => String(status || "").toLowerCase() === "paid";

const styles = StyleSheet.create({
  page: {
    paddingTop: 28,
    paddingBottom: 28,
    paddingHorizontal: 28,
    fontSize: 10.5,
    color: "#111827",
    backgroundColor: "#ffffff",
    fontFamily: "Helvetica",
  },

  // Top
  topRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 16,
    marginBottom: 14,
  },
  brand: { flex: 1 },
  meta: { width: 220 },

  title: { fontSize: 18, fontWeight: 700, marginBottom: 6 },
  subtitle: { color: "#6B7280", fontSize: 10 },

  logoRow: { flexDirection: "row", alignItems: "center", gap: 10 },
  logo: { width: 44, height: 44, objectFit: "contain" },

  companyName: { fontSize: 12, fontWeight: 700 },
  companyLine: { marginTop: 2, color: "#374151" },

  metaCard: {
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 10,
    padding: 12,
  },
  metaRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 6,
  },
  metaKey: { color: "#6B7280" },
  metaVal: { fontWeight: 700 },

  accentBar: { height: 4, borderRadius: 10, marginTop: 10 },

  divider: {
    height: 1,
    backgroundColor: "#E5E7EB",
    marginTop: 12,
    marginBottom: 12,
  },

  twoCol: { flexDirection: "row", gap: 14 },
  box: {
    flex: 1,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 10,
    padding: 12,
  },
  boxTitle: { fontSize: 11, fontWeight: 700, marginBottom: 8 },
  line: { marginTop: 3, color: "#374151" },

  // Paid stamp
  paidStampWrap: {
    position: "absolute",
    top: 78,
    right: 34,
    transform: "rotate(-12deg)",
    opacity: 0.9,
  },
  paidStamp: {
    borderWidth: 3,
    borderRadius: 10,
    paddingVertical: 8,
    paddingHorizontal: 16,
  },
  paidStampText: {
    fontSize: 20,
    fontWeight: 900,
    letterSpacing: 2,
  },

  // Table (auto paginate)
  tableWrap: {
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 10,
    overflow: "hidden",
  },
  thead: {
    flexDirection: "row",
    backgroundColor: "#F9FAFB",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    paddingVertical: 10,
    paddingHorizontal: 10,
  },
  th: { fontSize: 10, fontWeight: 700, color: "#374151" },

  row: {
    flexDirection: "row",
    paddingVertical: 10,
    paddingHorizontal: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#F3F4F6",
  },
  td: { color: "#111827" },

  colDesc: { flex: 1.8, paddingRight: 8 },
  colQty: { width: 50, textAlign: "right" },
  colRate: { width: 80, textAlign: "right" },
  colAmt: { width: 90, textAlign: "right" },

  // Bottom blocks
  bottomRow: { flexDirection: "row", gap: 14, marginTop: 12 },
  notes: {
    flex: 1.15,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 10,
    padding: 12,
  },
  totals: {
    flex: 0.85,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 10,
    padding: 12,
  },
  kv: { flexDirection: "row", justifyContent: "space-between", marginTop: 8 },
  kvKey: { color: "#6B7280" },
  kvVal: { fontWeight: 700 },

  grandRow: {
    marginTop: 10,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: "#E5E7EB",
  },
  grandVal: { fontSize: 12, fontWeight: 800 },

  // Payment instructions block
  paymentWrap: {
    marginTop: 12,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 10,
    padding: 12,
  },
  paymentGrid: { flexDirection: "row", gap: 14 },
  paymentCol: { flex: 1 },
  paymentItem: { marginTop: 6, color: "#374151" },
  paymentKey: { color: "#6B7280" },

  footer: {
    marginTop: 14,
    textAlign: "center",
    color: "#6B7280",
    fontSize: 9,
  },
});

export default function EstimateInvoicePDF({
  companyInfo,
  invoiceData,
  estimateData,
  items,
  accent = "#008080",
  currency = "NGN",
}) {
  const subTotal = Number(invoiceData?.subtotal ?? invoiceData?.total ?? 0);
  const vatRate = Number(invoiceData?.vat_rate || 0);

  const vatAmount =
    invoiceData?.vat_amount != null
      ? Number(invoiceData.vat_amount || 0)
      : subTotal * vatRate;

  const total =
    invoiceData?.grand_total != null
      ? Number(invoiceData.grand_total || 0)
      : subTotal + vatAmount;

  const paid = isPaid(invoiceData?.status);

  // Bank details (optional fields — safe fallbacks)
  const bankName =
    companyInfo?.bank_name || companyInfo?.bankName || companyInfo?.bank || "";
  const acctName = companyInfo?.account_name || companyInfo?.accountName || "";
  const acctNo =
    companyInfo?.account_number || companyInfo?.accountNumber || "";
  const instructions =
    companyInfo?.payment_instructions ||
    companyInfo?.paymentInstructions ||
    invoiceData?.payment_instructions ||
    "";

  const reference =
    invoiceData?.invoiceNumber || invoiceData?.estimateNumber || "INV";

  return (
    <Document>
      <Page size="A4" style={styles.page} wrap>
        {/* PAID stamp */}
        {paid && (
          <View style={styles.paidStampWrap} fixed>
            <View style={[styles.paidStamp, { borderColor: accent }]}>
              <Text style={[styles.paidStampText, { color: accent }]}>
                PAID
              </Text>
            </View>
          </View>
        )}

        {/* Top */}
        <View style={styles.topRow}>
          <View style={styles.brand}>
            <Text style={styles.title}>
              {invoiceData?.doc_title || "INVOICE"}
            </Text>
            <Text style={styles.subtitle}>
              Generated from Estimate • Keep for your records
            </Text>

            <View style={[styles.accentBar, { backgroundColor: accent }]} />

            <View style={{ marginTop: 10 }}>
              <View style={styles.logoRow}>
                {companyInfo?.logo_url ? (
                  <Image style={styles.logo} src={companyInfo.logo_url} />
                ) : null}
                <View>
                  <Text style={styles.companyName}>
                    {safe(estimateData?.companyName)}
                  </Text>
                  <Text style={styles.companyLine}>
                    {safe(companyInfo?.address)}
                  </Text>
                  <Text style={styles.companyLine}>
                    {companyInfo?.email
                      ? `Email: ${companyInfo.email}`
                      : "Email: —"}
                  </Text>
                  <Text style={styles.companyLine}>
                    {companyInfo?.phone_number
                      ? `Phone: ${companyInfo.phone_number}`
                      : "Phone: —"}
                  </Text>
                  <Text style={styles.companyLine}>
                    {companyInfo?.TIN ? `TIN: ${companyInfo.TIN}` : "TIN: —"}
                  </Text>
                </View>
              </View>
            </View>
          </View>

          <View style={styles.meta}>
            <View style={styles.metaCard}>
              <View style={styles.metaRow}>
                <Text style={styles.metaKey}>Invoice #</Text>
                <Text style={styles.metaVal}>{safe(reference)}</Text>
              </View>
              <View style={styles.metaRow}>
                <Text style={styles.metaKey}>Date</Text>
                <Text style={styles.metaVal}>{safe(invoiceData?.date)}</Text>
              </View>
              <View style={styles.metaRow}>
                <Text style={styles.metaKey}>Due</Text>
                <Text style={styles.metaVal}>
                  {safe(invoiceData?.expiryDate)}
                </Text>
              </View>
              <View style={styles.metaRow}>
                <Text style={styles.metaKey}>Status</Text>
                <Text style={styles.metaVal}>
                  {safe(invoiceData?.status || "Pending")}
                </Text>
              </View>

              <View style={{ marginTop: 10 }}>
                <View style={[styles.accentBar, { backgroundColor: accent }]} />
              </View>
            </View>
          </View>
        </View>

        <View style={styles.divider} />

        {/* Bill to + Payment meta */}
        <View style={styles.twoCol}>
          <View style={styles.box}>
            <Text style={styles.boxTitle}>BILL TO</Text>
            <Text style={styles.line}>{safe(invoiceData?.customer)}</Text>
            <Text style={styles.line}>{safe(invoiceData?.address)}</Text>
          </View>

          <View style={styles.box}>
            <Text style={styles.boxTitle}>PAYMENT SUMMARY</Text>
            <Text style={styles.line}>
              Currency: <Text style={{ fontWeight: 700 }}>{currency}</Text>
            </Text>
            <Text style={styles.line}>
              VAT:{" "}
              <Text style={{ fontWeight: 700 }}>
                {vatRate > 0 ? `${vatRate * 100}%` : "—"}
              </Text>
            </Text>
            <Text style={styles.line}>
              Reference:{" "}
              <Text style={{ fontWeight: 700 }}>{safe(reference)}</Text>
            </Text>
          </View>
        </View>

        <View style={styles.divider} />

        {/* TABLE (header repeats automatically with fixed) */}
        <View style={styles.tableWrap}>
          <View style={styles.thead} fixed>
            <Text style={[styles.th, styles.colDesc]}>Description</Text>
            <Text style={[styles.th, styles.colQty]}>Qty</Text>
            <Text style={[styles.th, styles.colRate]}>Rate</Text>
            <Text style={[styles.th, styles.colAmt]}>Amount</Text>
          </View>

          {(items || []).length ? (
            items.map((it, idx) => (
              <View
                key={idx}
                style={styles.row}
                wrap={false} // keeps the row from splitting across pages
              >
                <Text style={[styles.td, styles.colDesc]}>
                  {safe(it.description, "")}
                </Text>
                <Text style={[styles.td, styles.colQty]}>
                  {Number(it.quantity || 0) || 0}
                </Text>
                <Text style={[styles.td, styles.colRate]}>
                  {money(it.unitPrice || 0, currency)}
                </Text>
                <Text style={[styles.td, styles.colAmt]}>
                  {money(it.lineTotal || 0, currency)}
                </Text>
              </View>
            ))
          ) : (
            <View style={styles.row}>
              <Text style={[styles.td, styles.colDesc, { color: "#6B7280" }]}>
                No items
              </Text>
              <Text style={[styles.td, styles.colQty]}> </Text>
              <Text style={[styles.td, styles.colRate]}> </Text>
              <Text style={[styles.td, styles.colAmt]}> </Text>
            </View>
          )}
        </View>

        {/* Notes + Totals */}
        <View style={styles.bottomRow} wrap>
          <View style={styles.notes}>
            <Text style={styles.boxTitle}>NOTES</Text>
            <Text style={{ color: "#374151", lineHeight: 1.35 }}>
              {invoiceData?.notes ? String(invoiceData.notes) : "—"}
            </Text>
          </View>

          <View style={styles.totals}>
            <Text style={styles.boxTitle}>TOTALS</Text>

            <View style={styles.kv}>
              <Text style={styles.kvKey}>Sub-Total</Text>
              <Text style={styles.kvVal}>{money(subTotal, currency)}</Text>
            </View>

            <View style={styles.kv}>
              <Text style={styles.kvKey}>
                VAT {vatRate > 0 ? `(${vatRate * 100}%)` : ""}
              </Text>
              <Text style={styles.kvVal}>{money(vatAmount, currency)}</Text>
            </View>

            <View style={[styles.kv, styles.grandRow]}>
              <Text style={[styles.kvKey, { fontWeight: 700 }]}>Total</Text>
              <Text style={styles.grandVal}>{money(total, currency)}</Text>
            </View>
          </View>
        </View>

        {/* Bank details / Payment instructions */}
        <View style={styles.paymentWrap} wrap>
          <Text style={styles.boxTitle}>PAYMENT INSTRUCTIONS</Text>

          <View style={styles.paymentGrid}>
            <View style={styles.paymentCol}>
              <Text style={styles.paymentItem}>
                <Text style={styles.paymentKey}>Bank:</Text>{" "}
                <Text style={{ fontWeight: 700 }}>{safe(bankName)}</Text>
              </Text>
              <Text style={styles.paymentItem}>
                <Text style={styles.paymentKey}>Account Name:</Text>{" "}
                <Text style={{ fontWeight: 700 }}>{safe(acctName)}</Text>
              </Text>
              <Text style={styles.paymentItem}>
                <Text style={styles.paymentKey}>Account Number:</Text>{" "}
                <Text style={{ fontWeight: 700 }}>{safe(acctNo)}</Text>
              </Text>
            </View>

            <View style={styles.paymentCol}>
              <Text style={styles.paymentItem}>
                <Text style={styles.paymentKey}>Payment Reference:</Text>{" "}
                <Text style={{ fontWeight: 700 }}>{safe(reference)}</Text>
              </Text>
              <Text style={styles.paymentItem}>
                <Text style={styles.paymentKey}>Note:</Text>{" "}
                <Text>
                  Please include the reference above in your transfer narration.
                </Text>
              </Text>
            </View>
          </View>

          {instructions ? (
            <View style={{ marginTop: 8 }}>
              <Text style={[styles.paymentKey, { marginBottom: 4 }]}>
                Additional Instructions:
              </Text>
              <Text style={{ color: "#374151", lineHeight: 1.35 }}>
                {String(instructions)}
              </Text>
            </View>
          ) : null}
        </View>

        <Text style={styles.footer}>
          Thank you for your business. Questions? Contact{" "}
          {companyInfo?.email || "us"}.
        </Text>
      </Page>
    </Document>
  );
}
